import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './index.module.sass';
import classNames from 'classnames/bind';
import blank from 'assets/blank.png';
import throttle from 'lodash/throttle';

export const useResize = (desktopSize = 1023, padSize = 767) => {
  const resizeRef = useRef();

  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    const resizeWin = throttle(() => {
      if (window.innerWidth > desktopSize && resizeRef.current !== 'desktop') {
        resizeRef.current = 'desktop';
        setDeviceType('desktop');
      } else if (window.innerWidth > padSize && window.innerWidth <= desktopSize && resizeRef.current !== 'pad') {
        resizeRef.current = 'pad';
        setDeviceType('pad');
      } else if (window.innerWidth <= padSize && resizeRef.current !== 'mobile') {
        resizeRef.current = 'mobile';
        setDeviceType('mobile');
      }
    }, 200);

    resizeWin();
    window.addEventListener('resize', resizeWin);
    return () => window.removeEventListener('resize', resizeWin);
  }, []); //eslint-disable-line

  return deviceType;
};

const Carousel = ({
  breakpoint = [1023, 767],
  loading = true,
  items = [],
  indicator = true,
  indicatorInsideCard = false,
  fullWidth = false,
  theme = 'dark',
  onSwipe = () => {},
}) => {
  const cx = classNames.bind(styles);
  const deviceSize = useResize(breakpoint[0], breakpoint[1]);
  const [index, setIndex] = useState(0);
  const [pointStart, setPointStart] = useState([]);
  const [pointMove, setPointMove] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const [direction, setDirection] = useState('');
  const [dragImage, setDragImage] = useState(undefined);
  const [carouselWidth, setCarouselWidth] = useState();
  const [markStretchDirection, setMarkStretchDirection] = useState('left');
  const [timestamp, setTimestamp] = useState();
  const windowWidth = deviceSize !== 'mobile' ? 480 : window.innerWidth;

  // marker
  const indicatorWidth = 5;
  const indicatorMargin = 3;
  const indicatorGroupWidth = indicatorWidth + indicatorMargin;
  const moveRate = Math.abs(pointMove) / windowWidth;
  const overHalf = moveRate >= 0.5;
  const listLength = items.length;
  const isFirstIndex = index === 0;
  const isLastIndex = index === listLength - 1;

  const carouselRef = useCallback(node => {
    if (node !== null) {
      setCarouselWidth(node.scrollWidth);
    }
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = blank;
    setDragImage(img);
  }, []);

  //   moveing method --------v
  const preventDefault = useCallback(
    e => {
      if (isMoving) {
        e.preventDefault();
      }
    },
    [isMoving]
  );

  useEffect(() => {
    window.addEventListener('touchmove', preventDefault, { passive: false });
    return () => {
      window.removeEventListener('touchmove', preventDefault, { passive: false });
    };
  }, [isMoving, preventDefault]);

  useEffect(() => {
    if (isMoving) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'inherit';
    }
  }, [isMoving]);

  // for indicator
  const setIndicatorMarkerDirection = result => {
    if (Math.abs(result) > windowWidth / 2) {
      // 準備縮回
      if (result > 0) {
        setMarkStretchDirection('right');
      } else {
        setMarkStretchDirection('left');
      }
    } else {
      // 準備伸展
      if (result > 0) {
        setMarkStretchDirection('left');
      } else {
        setMarkStretchDirection('right');
      }
    }
  };

  const swiping = distance => {
    if (Math.abs(pointMove) > distance) {
      return pointMove > 0 ? nextSlide() : previousSlide();
    } else {
      setIsMoving(false);
      setPointStart([]);
      setPointMove(0);
      setDirection('');
    }
    setTimestamp(0);
  };

  const dragStart = e => {
    e.dataTransfer.setDragImage(dragImage, 1, 1);
    setPointStart([e.pageX, e.pageY]);
    setTimestamp(Date.now());
    setIsMoving(true);
  };

  const dragMove = e => {
    e.preventDefault();
    if (e.pageX !== 0 && isMoving) {
      const result = pointStart[0] - e.pageX;
      setIsMoving(true);
      setPointMove(result);
      setIndicatorMarkerDirection(result);
    }
  };

  const dragEnd = e => {
    e.preventDefault();

    if (Date.now() - timestamp < 500) {
      swiping(windowWidth / 5);
    } else {
      swiping(windowWidth / 2);
    }
  };

  const touchStart = e => {
    setPointStart([e.touches[0].clientX, e.touches[0].clientY]);
    setTimestamp(Date.now());
  };

  const touchMove = e => {
    // 移動距離(touch)
    let result = pointStart[0] - e.touches[0].clientX;

    // 計算移動方向，若移動方向為垂直則不執行carousel，若為水平則移動carousel
    // 並且不會隨著移動的斜率改變方向
    if (e.touches && direction === '') {
      const endY = e.touches[0].clientY,
        startY = pointStart[1],
        endX = e.touches[0].clientX,
        startX = pointStart[0];
      const m = (endY - startY) / (endX - startX);
      if (m < 1 && m > -1) {
        setIsMoving(true);
        setPointMove(result);
        setDirection('horizonal');
        setIndicatorMarkerDirection(result);
      } else {
        setDirection('vertical');
      }
    } else if (e.touches && direction === 'horizonal') {
      setPointMove(result);
      setIndicatorMarkerDirection(result);
    }
  };

  const touchEnd = () => {
    if (Date.now() - timestamp < 500) {
      swiping(windowWidth / 5);
    } else {
      swiping(windowWidth / 2);
    }
  };

  const previousSlide = () => {
    let result = undefined;
    if (deviceSize === 'mobile') {
      result = index !== 0 ? index - 1 : 0;
    } else {
      result = index !== 0 ? index - 1 : index;
    }
    onSwipe(result);
    setIndex(result);
    setIsMoving(false);
    setPointStart([]);
    setPointMove(0);
    setDirection('');
  };

  const nextSlide = () => {
    let result = undefined;
    if (deviceSize === 'mobile') {
      result = index !== listLength - 1 ? index + 1 : index;
    } else {
      result = index !== Math.ceil(listLength) - 1 ? index + 1 : index;
    }
    onSwipe(result);
    setIndex(result);
    setIsMoving(false);
    setPointStart([]);
    setPointMove(0);
    setDirection('');
  };

  const itemWidth = useCallback(() => {
    if (fullWidth) {
      return windowWidth - 40;
    } else {
      return carouselWidth;
    }
  }, [fullWidth, windowWidth, carouselWidth]);

  const ulOffset = useCallback(() => {
    if (fullWidth) {
      let width = itemWidth() + 10;
      let offset = 15;
      return -(index * width + pointMove) + offset;
    } else {
      return -(index * itemWidth() + pointMove);
    }
  }, [pointMove, index, fullWidth, itemWidth]);

  const markerWidth = useCallback(() => {
    const indecatorsWidth = indicatorWidth * 3 + indicatorMargin * 2;

    // 在第一個及最後一個 indicator 時不做變化
    if ((isFirstIndex && pointMove <= 0) || (isLastIndex && pointMove > 0)) return indicatorWidth;

    let overHalf = moveRate >= 0.5;
    if (overHalf) {
      return indecatorsWidth - moveRate * 2 * (indicatorWidth + indicatorMargin);
    } else {
      return indicatorWidth + moveRate * 2 * (indicatorWidth + indicatorMargin);
    }
  }, [pointMove, isFirstIndex, isLastIndex, moveRate]);

  const markerDirectionOffset = useCallback(() => {
    const goingNext = pointMove > 0;

    if (isMoving) {
      // 拖曳中
      if (goingNext) {
        if (overHalf) {
          if (isLastIndex) return 0;
          return (listLength - 2 - index) * indicatorGroupWidth;
        } else {
          return index * indicatorGroupWidth;
        }
      } else {
        if (overHalf) {
          if (isFirstIndex) return 0;
          return (index - 1) * indicatorGroupWidth;
        } else {
          return (listLength - 1 - index) * indicatorGroupWidth;
        }
      }
    } else {
      return markStretchDirection === 'left'
        ? index * indicatorGroupWidth
        : (listLength - 1 - index) * indicatorGroupWidth;
    }
  }, [
    markStretchDirection,
    index,
    indicatorGroupWidth,
    isFirstIndex,
    isLastIndex,
    isMoving,
    listLength,
    overHalf,
    pointMove,
  ]);

  // item style
  const Indicators = () => {
    if (!indicator) return '';
    if (listLength <= 1) return '';

    const indicatorContainerClass = cx({
      'indicator-container': true,
      inside: indicatorInsideCard,
      [theme]: true,
    });

    let indicatorList = [];
    for (let i = 0; i < listLength; i++) {
      const indicatorClass = cx({
        indicator: true,
        active: i === index,
      });
      indicatorList.push(<li className={indicatorClass} key={i}></li>);
    }

    let width = indicatorWidth * listLength + indicatorMargin * (listLength - 1);
    return (
      <ul
        className={indicatorContainerClass}
        style={{
          width,
          left: indicatorInsideCard ? `calc(50% - ${width / 2}px)` : 'inherit',
        }}
      >
        <div
          className={styles.mark}
          style={{
            transition: isMoving ? '' : 'all 0.2s ease',
            width: `${markerWidth()}px`,
            [markStretchDirection]: `${markerDirectionOffset()}px`,
          }}
        />
        {indicatorList}
      </ul>
    );
  };

  const carouselClass = cx({
    carousel: true,
    'full-width': fullWidth,
  });

  const content = () => {
    if (loading) return <></>;
    if (listLength === 0) return <></>;
    return (
      <ul
        className={styles['carousel-ul']}
        style={{
          transform: `translateX(${ulOffset()}px)`,
          transition: isMoving ? '' : 'all 0.2s ease 0s',
        }}
      >
        {items.map((item, i) => {
          return (
            <li
              key={i}
              className={styles['carousel-item']}
              style={{ flex: `0 0 ${itemWidth()}px`, margin: fullWidth ? '0 5px' : 0 }}
              onTouchStart={touchStart}
              onTouchMove={touchMove}
              onTouchEnd={touchEnd}
              draggable
              onDragStart={dragStart}
              onDrag={dragMove}
              onDragEnd={dragEnd}
            >
              {item}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={carouselClass} style={{ width: '100%' }} ref={carouselRef}>
      <div className={styles['carousel-wrapper']} style={{ width: '100%', padding: fullWidth ? '12px 0' : 0 }}>
        {content()}
      </div>
      {Indicators()}
    </div>
  );
};

export default Carousel;
